import { useEffect, useMemo } from "react";
import { calculateOrderTotalsWithShipping, resolveShippingCents } from "@glastotaal/core";
import { formatEuro } from "@/lib/format";
import { btwAmount, btwLabel, useBtwPreference } from "@/lib/btw";
// Rechtstreeks, niet via de features/account-barrel — zie SiteLayout.tsx voor de reden.
import { useEffectiveDiscount } from "@/features/account/useEffectiveDiscount";
import { useT } from "@/lib/i18n";
import { cdnImage, IMG } from "@/lib/img";
import { useShippingConfig, useBewerkingsTarieven } from "@/lib/settings";
import { cartItemPrice, cartOnceExtras } from "./cart-pricing";
import { useCatalogOverrides } from "@/lib/catalog-overrides";
import { usePromotions } from "@/lib/promotions";
import { useCart } from "./cart-context";
import { isStormWindowItem, lineQuantity, isVrachtItem, isMontageLine } from "./types";
import { savedShippingCountry } from "@/features/checkout/saved-country";
import { useBulkIndex } from "@/lib/article-bulk";

interface Props {
  onClose: () => void;
  onViewCart: () => void;
  onCheckout: () => void;
}

/** Mini-cart dropdown anchored under the header cart icon (click to open). */
export function CartFlyout({ onClose, onViewCart, onCheckout }: Props) {
  const t = useT();
  const { items } = useCart();

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const shippingConfig = useShippingConfig();
  // Zelfde btw-basis en dezelfde korting als de winkelwagenpagina. Stond dat hier niet, dan las je
  // in dit uitklapmenu bedragen excl. btw terwijl de wagen ze incl. btw toont — 21% verschil op
  // precies hetzelfde artikel, en niets dat uitlegt waarom.
  const [pref] = useBtwPreference();
  const discount = useEffectiveDiscount();
  const promos = usePromotions();
  // Staffelprijzen: alleen laden als er een artikelregel in de wagen ligt.
  const bulk = useBulkIndex(items.some((i) => i.kind === "article"));
  // Offerte-modus: bevat de winkelwagen een prijs-op-aanvraag-item → geen directe
  // betaling; prijs en verzendkosten volgen in de offerte (zie CartPage/CheckoutPage).
  const isQuote = items.some((i) => i.kind === "article" && i.onRequest);
  // Admin-prijslaag meegeven (zie CartPage) zodat flyout, wagen en bestelling gelijk lopen.
  const overrides = useCatalogOverrides();
  // Inkooptarieven bewerkingen: dezelfde die de server bij het afrekenen gebruikt.
  const opsTarieven = useBewerkingsTarieven();
  const priced = useMemo(() => items.map((item) => ({ item, price: cartItemPrice(item, overrides, promos, bulk, opsTarieven) })), [items, overrides, promos, bulk]);
  const onceExtras = useMemo(() => cartOnceExtras(items), [items]);
  const totals = useMemo(() => {
    // Montage-regels tellen niet mee (die levert en plaatst de montagepartner) — net als op de
    // winkelwagenpagina, anders wijkt het totaal hier af.
    const pricedNormal = priced.filter((p) => !isMontageLine(p.item));
    const lines = [...pricedNormal.map((p) => p.price?.subtotalExcl ?? 0), ...onceExtras.map((e) => e.amountCents)];
    const subtotal = lines.reduce((s, l) => s + l, 0);
    // BENGglas heeft een eigen speciaal transport; "glas" = gewoon glas/isolatieglas op maat.
    const hasBenglas = pricedNormal.some((p) => p.item.kind === "benglas");
    const hasGlass = pricedNormal.some((p) => p.item.kind === "glass" || p.item.kind === "isolatieglas" || p.item.kind === "shower" || isStormWindowItem(p.item));
    // Lang artikel (>2500 mm) gaat ook met eigen transport, maar heeft een eigen tarief dat van
    // het glastarief mag afwijken (31-10-2026). Daarom niet meer als 'glas' meegeteld.
    const hasLongArticles = pricedNormal.some((p) => isVrachtItem(p.item, overrides));
    const hasOtherItems = pricedNormal.some((p) => p.item.kind === "article");
    const d = discount ? { kind: discount.kind, value: discount.value } : undefined;
    return calculateOrderTotalsWithShipping(lines, resolveShippingCents(subtotal, hasGlass, shippingConfig, savedShippingCountry(), { hasBenglas, hasOtherItems, hasLongArticles }), d);
  }, [priced, onceExtras, shippingConfig, discount, overrides]);

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-label={t("Winkelwagen-overzicht")}
        className="absolute right-0 top-full z-50 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-primary-100 bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-primary-600">{t("Uw winkelwagen is leeg.")}</p>
        ) : (
          <>
            <p className="mb-2 text-right text-[11px] text-primary-600">{t("Bedragen")} {btwLabel(pref)}</p>
            <ul className="max-h-72 space-y-3 overflow-auto">
              {priced.map(({ item, price }) => (
                <li key={item.id} className="flex gap-3">
                  {item.display.imageUrl ? (
                    <img src={cdnImage(item.display.imageUrl, { w: IMG.thumb })} alt="" loading="lazy" decoding="async" className="h-12 w-12 shrink-0 rounded-md object-cover" />
                  ) : (
                    <div className="h-12 w-12 shrink-0 rounded-md bg-primary-50" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-primary-900">{item.display.title}</p>
                    <p className="text-xs text-primary-600">
                      {lineQuantity(item)}× · {item.display.lines[1] ?? ""}
                    </p>
                    {item.kind === "article" && item.surcharges?.filter((s) => !s.onceKey).map((s, i) => (
                      <p key={i} className="truncate text-xs text-primary-600">+ {s.label}</p>
                    ))}
                  </div>
                  <span className="shrink-0 text-sm tabular-nums text-primary-900">
                    {item.kind === "article" && item.onRequest
                      ? <em className="not-italic text-xs text-primary-600">{t("Prijs volgt")}</em>
                      : price ? formatEuro(btwAmount(price.subtotalExcl, pref)) : "—"}
                  </span>
                </li>
              ))}
            </ul>

            <dl className="mt-3 space-y-1 border-t border-primary-100 pt-3 text-sm">
              {isQuote ? (
                <div className="flex justify-between font-medium text-primary-700">
                  <dt>{t("Verzendkosten & totaal")}</dt>
                  <dd className="italic text-primary-600">{t("volgt in de offerte")}</dd>
                </div>
              ) : (
                <>
                  {totals.discount != null && (
                    <div className="flex justify-between font-medium text-green-700">
                      <dt>{t("Korting")}{discount?.kind === "percent" ? ` (${discount.value}%)` : ""}</dt>
                      <dd className="tabular-nums">−{formatEuro(btwAmount(totals.discount, pref))}</dd>
                    </div>
                  )}
                  <div className="flex justify-between text-primary-600">
                    <dt>{t("Verzendkosten")} {btwLabel(pref)}</dt>
                    <dd className="tabular-nums text-primary-900">{formatEuro(btwAmount(totals.shipping, pref))}</dd>
                  </div>
                  <div className="flex justify-between font-semibold text-primary-900">
                    <dt>{t("Totaal incl. btw")}</dt>
                    <dd className="tabular-nums">{formatEuro(totals.total)}</dd>
                  </div>
                </>
              )}
            </dl>

            <div className="mt-4 space-y-2">
              <button
                type="button"
                onClick={onCheckout}
                className="min-h-11 w-full rounded-lg bg-buy-600 px-4 font-semibold text-white transition hover:bg-buy-700"
              >
                {isQuote ? t("Offerte aanvragen") : t("Afrekenen")}
              </button>
              <button
                type="button"
                onClick={onViewCart}
                className="min-h-11 w-full rounded-lg border border-primary-200 px-4 font-medium text-primary-700 transition hover:bg-primary-50"
              >
                {t("Bekijk winkelwagen")}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
